import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Spin,
  Button,
  Avatar,
  Typography,
  Descriptions,
  Tag,
  Timeline,
  Statistic,
  Space,
  notification,
  Alert,
  Badge,
  Breadcrumb,
  Form,
  Input,
  Select,
  DatePicker,
} from "antd";
import {
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  IdcardOutlined,
  HomeOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import { UserContext } from "../../../context/User.context";
import { useHttp } from "../../../utils/DataHendler";
import { useMediaQuery } from "react-responsive";

const { Title, Text } = Typography;

export const DetailUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { getData, patchData } = useHttp();
  const { user } = useContext(UserContext);
  
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [api, contextHolder] = notification.useNotification();
  const [isEditMode, setIsEditMode] = useState(false);
  const [form] = Form.useForm();
  const [shifts, setShifts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id && user) {
      fetchUserDetail();
    }
  }, [id, user]);

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const response = await getData(`/users/${id}`, {}, true);
      
      if (response.success) {
        setUserData(response.data);
        // Initialize form with current data
        form.setFieldsValue({
          username: response.data.username,
          email: response.data.email || '',
          phone: response.data.phone || '',
          role_id: response.data.role.id,
          security_shift_id: response.data.security_shift?.id || null,
          name: response.data.profile?.name || '',
          nik: response.data.profile?.nik || '',
          address: response.data.profile?.address || '',
          birthdate: response.data.profile?.birthdate || '',
          photo_url: response.data.profile?.photo_url || '',
        });
      } else {
        api["error"]({
          message: "Failed to Load User",
          description: "Unable to fetch user details",
        });
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      api["error"]({
        message: "Error",
        description: "Failed to load user details",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchShifts = async () => {
    setLoadingShifts(true);
    try {
      const response = await getData("/shift/lookup", {}, true);
      if (response.success) {
        setShifts(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching shifts:", error);
    } finally {
      setLoadingShifts(false);
    }
  };

  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await getData("/roles/lookup", {}, true);
      if (response.success) {
        setRoles(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
    fetchRoles();
    if (userData.role.code === "R-003") {
      fetchShifts();
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    form.resetFields();
    // Reset form to original values
    form.setFieldsValue({
      username: userData.username,
      email: userData.email || '',
      phone: userData.phone || '',
      role_id: userData.role.id,
      security_shift_id: userData.security_shift?.id || null,
      name: userData.profile?.name || '',
      nik: userData.profile?.nik || '',
      address: userData.profile?.address || '',
      birthdate: userData.profile?.birthdate || '',
      photo_url: userData.profile?.photo_url || '',
    });
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        id: parseInt(id),
        username: values.username,
        email: values.email || null,
        phone: values.phone || null,
        role_id: values.role_id,
        security_shift_id: values.security_shift_id || null,
        profile: {
          name: values.name || null,
          nik: values.nik || null,
          address: values.address || null,
          birthdate: values.birthdate || null,
          photo_url: values.photo_url || null,
        }
      };

      const response = await patchData(`/users`, payload, {}, true);
      
      if (response.success) {
        api["success"]({
          message: "User Updated Successfully",
          description: "User information has been updated",
        });
        setIsEditMode(false);
        fetchUserDetail(); // Refresh user data
      } else {
        throw new Error(response.message || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      api["error"]({
        message: "Update Failed",
        description: error.message || "Failed to update user information",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/user/users-list");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading user details...</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={{ padding: "20px" }}>
        <Alert
          message="User Not Found"
          description="The requested user could not be found."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div style={{ padding: isMobile ? "10px" : "20px" }}>
        {/* Header with Breadcrumb */}
        <div style={{ marginBottom: "24px" }}>
          <Breadcrumb
            style={{ marginBottom: "16px" }}
            items={[
              {
                title: "Dashboard",
                href: "/user/users-list",
              },
            //   {
            //     title: "Users",
            //     href: "/user/users",
            //   },
              {
                title: userData.username,
              },
            ]}
          />
          
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "12px" : "0"
          }}>
            <Title level={2} style={{ margin: 0 }}>User Details</Title>
            <Space>
              {/* <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
              >
                Back to Users
              </Button> */}
              {!isEditMode ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  Edit User
                </Button>
              ) : (
                <Space>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={submitting}
                    onClick={() => form.submit()}
                  >
                    Save Changes
                  </Button>
                </Space>
              )}
            </Space>
          </div>
        </div>

        {/* Main Content */}
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Row gutter={[16, 16]}>
            {/* User Profile Section */}
            <Col xs={24} lg={8}>
              <Card 
                title={
                  <Space>
                    <UserOutlined />
                    Profile Information
                  </Space>
                }
              >
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  <Avatar
                    size={100}
                    src={userData.profile?.photo_url}
                    icon={<UserOutlined />}
                    style={{ marginBottom: "10px" }}
                  />
                  <div>
                    <Title level={4} style={{ margin: "0 0 5px 0" }}>
                      {isEditMode ? (
                        <Form.Item 
                          name="name" 
                          style={{ margin: 0 }}
                        >
                          <Input 
                            placeholder="Full Name"
                            style={{ textAlign: "center", fontWeight: "bold" }}
                          />
                        </Form.Item>
                      ) : (
                        userData.profile?.name || "No Name"
                      )}
                    </Title>
                    <Text type="secondary">
                      @{isEditMode ? (
                        <Form.Item 
                          name="username" 
                          style={{ margin: 0, display: "inline-block" }}
                          rules={[{ required: true, message: 'Username is required' }]}
                        >
                          <Input 
                            size="small"
                            style={{ width: "120px" }}
                          />
                        </Form.Item>
                      ) : userData.username}
                    </Text>
                  </div>
                </div>
                
                {isEditMode ? (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Form.Item
                      name="phone"
                      label={<><PhoneOutlined /> Phone</>}
                    >
                      <Input placeholder="Phone number" />
                    </Form.Item>
                    
                    <Form.Item
                      name="email"
                      label={<><MailOutlined /> Email</>}
                      rules={[{ type: 'email', message: 'Please enter valid email' }]}
                    >
                      <Input placeholder="Email address" />
                    </Form.Item>
                    
                    <Form.Item
                      name="nik"
                      label={<><IdcardOutlined /> NIK</>}
                    >
                      <Input placeholder="National ID number" />
                    </Form.Item>
                    
                    <Form.Item
                      name="birthdate"
                      label={<><CalendarOutlined /> Birthdate</>}
                    >
                      <Input type="date" />
                    </Form.Item>
                    
                    <Form.Item
                      name="address"
                      label={<><HomeOutlined /> Address</>}
                    >
                      <Input.TextArea 
                        placeholder="Full address"
                        rows={3}
                      />
                    </Form.Item>

                    <Form.Item
                      name="role_id"
                      label="Role"
                      rules={[{ required: true, message: 'Role is required' }]}
                    >
                      <Select
                        placeholder="Select role"
                        loading={loadingRoles}
                        options={roles.map(role => ({
                          value: role.id,
                          label: `${role.name} (${role.code})`,
                        }))}
                      />
                    </Form.Item>

                    {form.getFieldValue('role_id') && roles.find(r => r.id === form.getFieldValue('role_id'))?.code === 'R-003' && (
                      <Form.Item
                        name="security_shift_id"
                        label="Security Shift"
                      >
                        <Select
                          placeholder="Select shift"
                          loading={loadingShifts}
                          allowClear
                          options={shifts.map(shift => ({
                            value: shift.id,
                            label: `${shift.description} (${shift.start_time} - ${shift.end_time})`,
                          }))}
                        />
                      </Form.Item>
                    )}
                  </Space>
                ) : (
                  <Descriptions column={1} size="small">
                    <Descriptions.Item 
                      label={<><PhoneOutlined /> Phone</>}
                    >
                      {userData.phone || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label={<><MailOutlined /> Email</>}
                    >
                      {userData.email || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label={<><IdcardOutlined /> NIK</>}
                    >
                      {userData.profile?.nik || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label={<><CalendarOutlined /> Birthdate</>}
                    >
                      {userData.profile?.birthdate || "Not provided"}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label={<><HomeOutlined /> Address</>}
                    >
                      <Text style={{ wordBreak: "break-word" }}>
                        {userData.profile?.address || "Not provided"}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </Card>
            </Col>

          {/* Role & Security Section */}
          <Col xs={24} lg={16}>
            <Row gutter={[16, 16]}>
              {/* Role Information */}
              <Col xs={24} md={12}>
                <Card 
                  title={
                    <Space>
                      <SafetyCertificateOutlined />
                      Role Information
                    </Space>
                  }
                >
                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    <Badge.Ribbon text={userData.role.code} color="blue">
                      <div style={{ 
                        background: "#f6ffed", 
                        border: "1px solid #b7eb8f", 
                        borderRadius: "6px", 
                        padding: "16px",
                        minHeight: "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column"
                      }}>
                        <Title level={4} style={{ margin: "0 0 8px 0", color: "#52c41a" }}>
                          {userData.role.name}
                        </Title>
                        <Text type="secondary">
                          {userData.role.description || "No description available"}
                        </Text>
                      </div>
                    </Badge.Ribbon>
                  </div>
                </Card>
              </Col>

              {/* Inspection Statistics - Only for SECURITY role */}
              {userData.role.code === "R-003" && (
                <Col xs={24} md={12}>
                  <Card 
                    title={
                      <Space>
                        <BarChartOutlined />
                        Inspection Statistics
                      </Space>
                    }
                  >
                    <Row gutter={16}>
                      <Col span={24}>
                        <Statistic
                          title="Total Inspections"
                          value={userData.inspection_statistics.total_inspections}
                          valueStyle={{ color: "#3f8600" }}
                          suffix="inspections"
                        />
                      </Col>
                    </Row>
                    <Row gutter={16} style={{ marginTop: "16px" }}>
                      <Col span={12}>
                        <Statistic
                          title="Today"
                          value={userData.inspection_statistics.today_inspections}
                          valueStyle={{ color: "#1890ff" }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Last 30 Days"
                          value={userData.inspection_statistics.last_30_days_inspections}
                          valueStyle={{ color: "#722ed1" }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              )}

              {/* Security Shift Details - Only for SECURITY role */}
              {userData.role.code === "R-003" && userData.security_shift && (
                <Col xs={24}>
                  <Card 
                    title={
                      <Space>
                        <ClockCircleOutlined />
                        Security Shift Details
                      </Space>
                    }
                  >
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <div style={{ marginBottom: "16px" }}>
                          <Text strong>Shift Description:</Text>
                          <div style={{ marginTop: "4px" }}>
                            <Tag color={userData.security_shift.active ? "green" : "red"}>
                              {userData.security_shift.description || "No description"}
                            </Tag>
                          </div>
                        </div>
                        <div>
                          <Text strong>Working Hours:</Text>
                          <div style={{ marginTop: "4px" }}>
                            <Text code>
                              {userData.security_shift.start_time} - {userData.security_shift.end_time}
                            </Text>
                          </div>
                        </div>
                      </Col>
                      
                      <Col xs={24} md={16}>
                        <Text strong>Patrol Intervals:</Text>
                        <Timeline
                          mode="left"
                          style={{ marginTop: "8px" }}
                          items={userData.security_shift.formatted_intervals?.map((interval, index) => ({
                            color: index === 0 ? "green" : index === userData.security_shift.formatted_intervals.length - 1 ? "red" : "blue",
                            children: (
                              <div>
                                <Text strong>Interval {interval.interval}</Text>
                                <br />
                                <Text type="secondary">{interval.time_range}</Text>
                              </div>
                            ),
                          })) || []}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              )}

              {/* Audit Information */}
              <Col xs={24}>
                <Card title="Audit Information" size="small">
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Created By">
                          {userData.audit.created_by || "System"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Created At">
                          {userData.audit.created_at}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Updated By">
                          {userData.audit.updated_by || "System"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Updated At">
                          {userData.audit.updated_at}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
      </div>
    </>
  );
};
